import React, { useState, useEffect } from 'react';
import { 
  Users, ChevronDown, ChevronUp, Check, Loader2, 
  Shield, Building2, FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category labels and icons for display
const CATEGORY_CONFIG = {
  banks: { label: 'Bank Accounts', icon: Building2 },
  wills: { label: 'Will Documents', icon: FileText },
  mutual_funds: { label: 'Mutual Funds', icon: FileText },
  insurance: { label: 'Insurance Policies', icon: Shield },
  crypto: { label: 'Cryptocurrency', icon: FileText },
  property: { label: 'Properties', icon: Building2 },
  demat: { label: 'Demat Accounts', icon: FileText },
  others: { label: 'Other Documents', icon: FileText }
};

const PartnerSharing = () => {
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [vaultEntries, setVaultEntries] = useState([]);
  const [sharedEntryIds, setSharedEntryIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const clientToken = localStorage.getItem('clientToken');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${clientToken}` }
  };

  // Fetch partner info
  const fetchPartnerInfo = async () => {
    try {
      const response = await axios.get(`${API}/client/partner-info`, axiosConfig);
      setPartnerInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch partner info:', error);
    }
  };

  // Fetch vault entries
  const fetchVaultEntries = async () => {
    try {
      const response = await axios.get(`${API}/client/vault/entries`, axiosConfig);
      const entries = response.data.entries || [];
      setVaultEntries(entries);
      
      // Extract currently shared entry IDs
      const shared = entries
        .filter(entry => entry.share_with_advisor === true)
        .map(entry => entry.id);
      setSharedEntryIds(shared);
    } catch (error) {
      console.error('Failed to fetch vault entries:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchPartnerInfo(), fetchVaultEntries()]);
      setIsLoading(false);
    };
    loadData();
  }, []);

  // Toggle sharing for a single entry
  const toggleEntrySharing = async (entryId) => {
    const isCurrentlyShared = sharedEntryIds.includes(entryId);
    const newSharedIds = isCurrentlyShared
      ? sharedEntryIds.filter(id => id !== entryId)
      : [...sharedEntryIds, entryId];
    
    // Optimistic update
    setSharedEntryIds(newSharedIds);
    setSavingId(entryId);

    try {
      await axios.patch(
        `${API}/client/vault/entry/${entryId}/share`,
        { share_with_advisor: !isCurrentlyShared },
        axiosConfig
      );
      
      // Update local vault entries state
      setVaultEntries(prev => prev.map(entry => 
        entry.id === entryId 
          ? { ...entry, share_with_advisor: !isCurrentlyShared }
          : entry
      ));
      
      toast.success(
        !isCurrentlyShared 
          ? 'Entry shared with partner' 
          : 'Entry unshared from partner'
      );
    } catch (error) {
      // Revert on error
      setSharedEntryIds(sharedEntryIds);
      toast.error('Failed to update sharing preference');
      console.error('Error toggling share:', error);
    } finally {
      setSavingId(null);
    }
  };

  // Group entries by category
  const groupedEntries = vaultEntries.reduce((acc, entry) => {
    const category = entry.category || 'others';
    if (!acc[category]) acc[category] = [];
    acc[category].push(entry);
    return acc;
  }, {});

  // Count shared entries
  const sharedCount = sharedEntryIds.length;
  const totalCount = vaultEntries.length;

  if (isLoading) {
    return (
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
            <span className="ml-2 text-slate-400">Loading...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="partner-sharing-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-amber-400" />
          Partner Sharing
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Partner Info */}
        {partnerInfo && (
          <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Your Partner</p>
                <p className="text-white font-medium">
                  {partnerInfo.partner_name || partnerInfo.company_name || 'Your Advisor'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                <span className="text-xs text-emerald-400">Connected</span>
              </div>
            </div>
          </div>
        )}

        {/* Sharing Summary */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-400">
            Sharing <span className="text-amber-400 font-medium">{sharedCount}</span> of {totalCount} vault entries
          </span>
        </div>

        {/* No Entries Message */}
        {vaultEntries.length === 0 ? (
          <div className="text-center py-6 text-slate-500">
            <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No vault entries yet</p>
            <p className="text-xs mt-1">Add entries in Life Vault to share with your partner</p>
          </div>
        ) : (
          <>
            {/* Expandable Section Header */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full flex items-center justify-between p-3 bg-slate-800/30 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
              data-testid="expand-partner-sharing"
            >
              <span className="text-slate-300 text-sm font-medium">
                {isExpanded ? 'Hide' : 'Manage'} Shared Documents
              </span>
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </button>

            {/* Expandable Content */}
            {isExpanded && (
              <div className="space-y-4 pt-2" data-testid="partner-sharing-entries">
                {Object.entries(groupedEntries).map(([category, entries]) => {
                  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.others;
                  const Icon = config.icon;
                  
                  return (
                    <div key={category} className="space-y-2">
                      {/* Category Header */}
                      <div className="flex items-center gap-2 text-slate-400">
                        <Icon className="w-4 h-4" />
                        <span className="text-xs font-medium uppercase tracking-wide">
                          {config.label}
                        </span>
                        <span className="text-xs text-slate-600">
                          ({entries.filter(e => sharedEntryIds.includes(e.id)).length}/{entries.length} shared)
                        </span>
                      </div>

                      {/* Entries List */}
                      <div className="space-y-1 pl-6">
                        {entries.map(entry => {
                          const isShared = sharedEntryIds.includes(entry.id);
                          const isSaving = savingId === entry.id;
                          
                          return (
                            <div
                              key={entry.id}
                              className={`flex items-center justify-between p-2 rounded-lg transition-colors ${
                                isShared 
                                  ? 'bg-emerald-500/10 border border-emerald-500/20' 
                                  : 'bg-slate-800/30 border border-transparent hover:border-slate-700'
                              }`}
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className={`text-sm truncate ${isShared ? 'text-white' : 'text-slate-400'}`}>
                                  {entry.save_as || entry.title || entry.name || 'Untitled Entry'}
                                </span>
                                {isShared && (
                                  <Check className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                                )}
                              </div>
                              
                              <div className="flex items-center gap-2 ml-2">
                                {isSaving && (
                                  <Loader2 className="w-3 h-3 text-amber-400 animate-spin" />
                                )}
                                <Switch
                                  checked={isShared}
                                  onCheckedChange={() => toggleEntrySharing(entry.id)}
                                  disabled={isSaving}
                                  className="data-[state=checked]:bg-emerald-500"
                                  data-testid={`partner-share-toggle-${entry.id}`}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Info Note */}
        <p className="text-xs text-slate-500 mt-4">
          Shared entries are visible to your partner/advisor for estate planning purposes.
        </p>
      </CardContent>
    </Card>
  );
};

export default PartnerSharing;
